import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/primitives";
import { Bank, Upload, LinkSimple, CheckCircle, ArrowClockwise } from "@phosphor-icons/react";

function fmt(n) {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return sign + Math.abs(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function parseCsv(text) {
  // Simple CSV parser expecting header row: date,description,amount,reference
  const out = [];
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) return out;
  const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const idx = {
    date: header.indexOf("date"),
    description: header.indexOf("description"),
    amount: header.indexOf("amount"),
    reference: header.indexOf("reference"),
  };
  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i].match(/("([^"]|"")*"|[^,]+)(,|$)/g)
      ?.map((p) => p.replace(/,$/, "").replace(/^"|"$/g, "").replace(/""/g, '"'))
      || lines[i].split(",");
    if (!parts[idx.date]) continue;
    out.push({
      date: parts[idx.date].trim(),
      description: (parts[idx.description] || "").trim(),
      amount: Number(parts[idx.amount] || 0),
      reference: (parts[idx.reference] || "").trim() || null,
    });
  }
  return out;
}

export default function BankReconciliation() {
  const [stmts, setStmts] = useState([]);
  const [active, setActive] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  const [importForm, setImportForm] = useState({
    bank_account_code: "1111",
    statement_date: new Date().toISOString().slice(0, 10),
    csvText: "",
  });
  const [busy, setBusy] = useState(false);

  async function loadStmts() {
    setLoading(true);
    try {
      const { data } = await api.get("/bank/statements");
      setStmts(data || []);
      if (data && data.length && !active) selectStmt(data[0].id);
    } catch (e) { toast.error(formatError(e)); }
    setLoading(false);
  }
  useEffect(() => { loadStmts(); /* eslint-disable-next-line */ }, []);

  async function selectStmt(id) {
    setActive(id);
    try {
      const { data } = await api.get(`/bank/statements/${id}/lines`);
      setLines(data || []);
    } catch (e) { toast.error(formatError(e)); }
  }

  async function importStmt() {
    const parsed = parseCsv(importForm.csvText);
    if (parsed.length === 0) return toast.error("No lines parsed. Header expected: date,description,amount,reference");
    setBusy(true);
    try {
      await api.post("/bank/statements/import", {
        bank_account_code: importForm.bank_account_code,
        statement_date: importForm.statement_date,
        lines: parsed,
      });
      toast.success(`Imported ${parsed.length} lines`);
      setImportOpen(false);
      setImportForm({ ...importForm, csvText: "" });
      await loadStmts();
    } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  }

  async function matchLine(lid, journalId) {
    try {
      await api.post(`/bank/lines/${lid}/match`, { journal_id: journalId });
      toast.success("Reconciled");
      if (active) await selectStmt(active);
    } catch (e) { toast.error(formatError(e)); }
  }

  async function unmatchLine(lid) {
    try {
      await api.post(`/bank/lines/${lid}/unmatch`);
      if (active) await selectStmt(active);
    } catch (e) { toast.error(formatError(e)); }
  }

  const unclearedCount = lines.filter((l) => !l.cleared).length;

  return (
    <div data-testid="bank-rec-page">
      <PageHeader
        overline="Finance / Cash"
        title="Bank Reconciliation"
        subtitle="Import a statement, tick off cleared journals — everything left over is unreconciled."
        testId="bank-header"
      />

      <div className="phizo-card p-4 mb-4 flex items-center gap-3 flex-wrap">
        <Bank size={16} weight="bold" className="text-[#71717a]" />
        <select value={active || ""} onChange={(e) => selectStmt(e.target.value)}
                className="border border-[#e4e4e7] px-3 py-1.5 text-xs outline-none focus:border-black"
                data-testid="bank-stmt-select">
          {stmts.length === 0 && <option value="">No statements imported yet</option>}
          {stmts.map((s) => (
            <option key={s.id} value={s.id}>
              {s.statement_date} · {s.bank_account_code} · {s.line_count} lines
            </option>
          ))}
        </select>
        <div className="text-xs text-[#71717a]">
          {lines.length ? <span>{unclearedCount} uncleared of {lines.length}</span> : ""}
        </div>
        <button onClick={() => loadStmts()} className="border border-[#e4e4e7] px-3 py-1.5 text-xs hover:bg-[#f4f4f5]">
          <ArrowClockwise size={12} weight="bold" className="inline mr-1" /> Refresh
        </button>
        <button onClick={() => setImportOpen(true)} data-testid="bank-import-btn"
                className="ml-auto bg-black text-white px-4 py-1.5 text-xs flex items-center gap-1.5 hover:bg-[#27272a]">
          <Upload size={12} weight="bold" /> Import CSV
        </button>
      </div>

      <div className="phizo-card overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
            <tr className="text-left">
              <th className="px-3 py-2 phizo-label text-[9px]">Date</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Description</th>
              <th className="px-3 py-2 phizo-label text-[9px] text-right">Amount</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Status</th>
              <th className="px-3 py-2 phizo-label text-[9px]">Match candidates</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-6 text-center text-[#71717a]">Loading…</td></tr>}
            {!loading && lines.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-[#71717a]">
                Select a statement above or import a CSV to start reconciling.
              </td></tr>
            )}
            {lines.map((l) => (
              <tr key={l.id} className={`border-b border-[#f4f4f5] ${l.cleared ? "bg-[#f0fdf4]" : ""}`}
                  data-testid={`bank-line-${l.id.slice(0,8)}`}>
                <td className="px-3 py-2 mono text-[11px] whitespace-nowrap">{l.date}</td>
                <td className="px-3 py-2 max-w-[300px] truncate">{l.description}
                  {l.reference && <span className="ml-2 mono text-[10px] text-[#71717a]">({l.reference})</span>}
                </td>
                <td className={`px-3 py-2 text-right mono ${l.amount < 0 ? "text-[#991b1b]" : "text-[#166534]"}`}>
                  {fmt(l.amount)}
                </td>
                <td className="px-3 py-2">
                  {l.cleared ? (
                    <span className="inline-flex items-center gap-1 text-[10px] text-[#166534]">
                      <CheckCircle size={12} weight="fill" /> {l.matched_journal_number}
                    </span>
                  ) : (
                    <span className="text-[10px] text-[#a1a1aa]">Uncleared</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {l.cleared ? (
                    <button onClick={() => unmatchLine(l.id)} className="text-[10px] text-[#71717a] hover:underline">
                      undo
                    </button>
                  ) : (l.candidates || []).length === 0 ? (
                    <span className="text-[10px] text-[#a1a1aa]">no auto match</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {l.candidates.map((c) => (
                        <button key={c.journal_id} onClick={() => matchLine(l.id, c.journal_id)}
                                data-testid={`bank-match-${l.id.slice(0,8)}-${c.number}`}
                                className="inline-flex items-center gap-1 mono text-[10px] border border-[#e4e4e7] px-1.5 py-0.5 hover:bg-black hover:text-white">
                          <LinkSimple size={10} weight="bold" /> {c.number}
                        </button>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {importOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setImportOpen(false)}>
          <div className="bg-white w-full max-w-2xl" onClick={(e) => e.stopPropagation()} data-testid="bank-import-modal">
            <div className="px-5 py-3 border-b">
              <div className="phizo-label text-[10px] text-[#a1a1aa]">Bank reconciliation</div>
              <h3 className="font-heading text-xl font-black tracking-tighter">Import statement CSV</h3>
            </div>
            <div className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Bank account code</div>
                  <input value={importForm.bank_account_code}
                         onChange={(e) => setImportForm({ ...importForm, bank_account_code: e.target.value })}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm mono outline-none focus:border-black"/>
                </label>
                <label className="text-xs">
                  <div className="phizo-label text-[10px] mb-1">Statement date</div>
                  <input type="date" value={importForm.statement_date}
                         onChange={(e) => setImportForm({ ...importForm, statement_date: e.target.value })}
                         className="w-full border border-[#e4e4e7] px-3 py-1.5 text-sm outline-none focus:border-black"/>
                </label>
              </div>
              <label className="text-xs block">
                <div className="phizo-label text-[10px] mb-1">CSV rows — header: <span className="mono">date,description,amount,reference</span></div>
                <textarea value={importForm.csvText}
                          onChange={(e) => setImportForm({ ...importForm, csvText: e.target.value })}
                          placeholder="date,description,amount,reference&#10;2026-09-01,Opening balance,500000,OPN&#10;2026-09-07,Fuel,-3500,POS-1"
                          rows={10}
                          className="w-full border border-[#e4e4e7] px-3 py-2 text-xs mono outline-none focus:border-black"
                          data-testid="bank-csv-textarea"/>
              </label>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => setImportOpen(false)} className="border border-[#e4e4e7] px-4 py-1.5 text-xs hover:bg-[#f4f4f5]">Cancel</button>
              <button onClick={importStmt} disabled={busy} data-testid="bank-import-go"
                      className="bg-[#002FA7] text-white px-4 py-1.5 text-xs font-medium hover:bg-[#001f7a] disabled:opacity-60">
                {busy ? "Importing…" : "Import"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
